import React, { useMemo } from "react";
import { SupportChat, createSupportTransport } from "@ui/support-chat";
import type { HttpAdapter } from "@ui/support-chat";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";

/**
 * Mounts the shared support widget for signed-in tenants.
 *
 * Auth comes from the shared axios instance, whose request interceptor
 * attaches the bearer token — so the widget never touches localStorage or
 * knows how this app authenticates.
 */
const httpAdapter: HttpAdapter = {
  get: async <T,>(url: string): Promise<T> => (await api.get<T>(url)).data,
  post: async <T,>(url: string, body?: unknown): Promise<T> =>
    (await api.post<T>(url, body ?? {})).data,
};

const SupportChatWidget: React.FC = () => {
  const { user } = useAuth();

  // Stable across renders so the polling effect isn't torn down and rebuilt.
  const transport = useMemo(() => createSupportTransport(httpAdapter), []);

  // Support chat is for signed-in users only — identity and scoping both come
  // from the JWT, so there is nothing sensible to show a logged-out visitor.
  if (!user) return null;

  return (
    <SupportChat
      transport={transport}
    />
  );
};

export default SupportChatWidget;
